import React, { useEffect, useMemo, useState } from 'react';
import { Card, Steps, Tag, Space, Button, Form, Input, Typography, Select, DatePicker, Table, message } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { DebtTypeOptions, labelOfDebtType, labelOfAuditStatus, labelOfDebtStatus, labelOfCollectionStatus, toSelectOptions } from '../../constants/debtEnums';
import { formatAmountText } from '../../utils/numberFormat';
import { getMyDebts } from '../../services/debt';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './PublishNoticeSelectDebtPage.css';

const { Text } = Typography;

const KEY = 'swy_publish_notice_state';
const loadState = () => {
  try { return JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch (_) { return {}; }
};
const saveState = (next) => {
  const prev = loadState();
  sessionStorage.setItem(KEY, JSON.stringify({ ...prev, ...next }));
};

/**
 * 催告发布步骤1：选择债权
 * 加载并筛选当前用户的已发布债权，选择后进入模板选择步骤。
 */
const PublishNoticeSelectDebtPage = () => {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 });
  const [sorter, setSorter] = useState({ field: 'update_time', order: 'desc' });
  const [selectedDebtId, setSelectedDebtId] = useState(loadState().selectedDebtId || null);
  const selectedDebt = useMemo(() => records.find(d => d.id === selectedDebtId) || null, [records, selectedDebtId]);
  const [filterForm] = Form.useForm();

  const fetchList = async (pageCfg = pagination, sortCfg = sorter) => {
    try {
      setLoading(true);
      const values = filterForm.getFieldsValue();
      const params = {
        page: pageCfg.current,
        size: pageCfg.pageSize,
        debt_type: values.debt_type,
        keyword: values.keyword,
        start_date: values.date_range && values.date_range[0] ? values.date_range[0].format('YYYY-MM-DD') : undefined,
        end_date: values.date_range && values.date_range[1] ? values.date_range[1].format('YYYY-MM-DD') : undefined,
        sort_field: sortCfg.field,
        sort_order: sortCfg.order,
      };
      const { data } = await getMyDebts(params);
      const all = Array.isArray(data?.records) ? data.records : [];
      const recs = all.filter(r => r.status === 'PUBLISHED');
      setRecords(recs);
      setPagination({
        current: Number(data?.current || pageCfg.current),
        pageSize: normalizePageSize(data?.size || pageCfg.pageSize),
        total: Number(data?.total ?? (Array.isArray(recs) ? recs.length : 0)),
      });
    } catch (e) {
      message.error('加载已发布债权失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ current: 1, pageSize: pagination.pageSize });
  }, []);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchList({ current: 1, pageSize: pagination.pageSize });
  };

  const handleReset = () => {
    filterForm.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
    setSorter({ field: 'update_time', order: 'desc' });
    fetchList({ current: 1, pageSize: pagination.pageSize }, { field: 'update_time', order: 'desc' });
  };

  const openDetail = (id) => {
    if (!id) return;
    navigate(`/debt-management/detail/${id}`);
  };

  const openHistory = (record) => {
    const id = record?.id;
    if (!id) return;
    const sp = new URLSearchParams();
    sp.set('debtId', String(id));
    if (record?.debtNo) {
      sp.set('debtNo', String(record.debtNo));
    }
    navigate(`/debt-notice/history?${sp.toString()}`);
  };

  const columns = [
    { title: '编号', dataIndex: 'debtNo', key: 'debtNo', width: 160 },
    { title: '债权名称', dataIndex: 'title', key: 'title', ellipsis: true },
    { title: '债权类型', dataIndex: 'debtType', key: 'debtType', width: 140, render: (v) => labelOfDebtType(v) },
    { title: '本金金额', dataIndex: 'principalAmount', key: 'principalAmount', width: 120, sorter: true, render: (v) => formatAmountText(v) },
    { title: '合计金额', dataIndex: 'totalAmount', key: 'totalAmount', width: 120, sorter: true, render: (v) => formatAmountText(v) },
    { title: '发生日', dataIndex: 'debtOccurrenceDate', key: 'debtOccurrenceDate', width: 120, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: '审核状态', dataIndex: 'auditStatus', key: 'auditStatus', width: 120, render: (v) => {
      const color = v === 'REJECTED' ? 'red' : v === 'APPROVED' ? 'green' : 'default';
      return <Tag color={color}>{labelOfAuditStatus(v)}</Tag>;
    } },
    { title: '债权状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => {
      const color = v === 'PUBLISHED' ? 'green' : v === 'PENDING' ? 'orange' : 'default';
      return <Tag color={color}>{labelOfDebtStatus(v)}</Tag>;
    } },
    { title: '催收状态', dataIndex: 'collectionStatus', key: 'collectionStatus', width: 120, render: (v) => {
      const color = v === 'IN_COLLECTION' ? 'green' : v === 'PAUSED' ? 'orange' : v === 'CLOSED' ? 'red' : 'default';
      return <Tag color={color}>{labelOfCollectionStatus(v)}</Tag>;
    } },    
    { title: '更新时间', dataIndex: 'updateTime', key: 'updateTime', width: 160, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
    { title: '操作', key: 'action', width: 240, render: (_, record) => (
      <Space>
        <Button type="link" onClick={() => openDetail(record.id)}>详情</Button>
        <Button type="link" onClick={() => openHistory(record)}>催告历史</Button>
      </Space>
    ) },
  ];

  const next = () => {
    if (!selectedDebtId) {
      message.warning('请选择债权');
      return;
    }
    if (selectedDebt?.collectionStatus === 'PAUSED' || selectedDebt?.collectionStatus === 'CLOSED') {
      message.error('当前债权催收状态为暂停或已结案，不能发布催告');
      return;
    }
    saveState({ selectedDebtId });
    navigate(`/debt-notice/publish/choose-template/${selectedDebtId}`);
  };

  const steps = [
    { title: '选择债权' },
    { title: '选择模板' },
    { title: '编辑内容' },
    { title: '签章与预览' },
    { title: '生成催告函' },
    { title: '发送催告' },
    { title: '归档' },
  ];
  const currentStep = 0;

  return (
    <div className="page-container">
      <div className="page-content page-content-wide">
        <div className="page-title">发布催告</div>

        <Card className="card-compact" size="small">
          {isMobile ? (
            <div className="c-steps-mobile-header" aria-label="步骤信息">
              <div className="c-steps-mobile-left">
                <span className="c-steps-mobile-stepno">{`第 ${currentStep + 1} 步`}</span>
                <span className="c-steps-mobile-title">{steps[currentStep]?.title || ''}</span>
              </div>
              <div className="c-steps-mobile-progress">{`${currentStep + 1}/${steps.length}`}</div>
            </div>
          ) : (
            <Steps current={currentStep} size="small" items={steps} />
          )}
        </Card>

        <div className="mb-md">
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            wrap={!isMobile}
            style={{ width: isMobile ? '100%' : undefined }}
          >
            <Button disabled block={isMobile}>上一步</Button>
            <Button type="primary" onClick={next} block={isMobile}>下一步</Button>
            {selectedDebt && <Text type="secondary" className="u-ellipsis-inline">{`已选择：${selectedDebt.title}`}</Text>}
          </Space>
        </div>

        <Card className="card-compact" size="small">
          <Form layout={isMobile ? 'vertical' : 'inline'} form={filterForm} onFinish={handleSearch}>
            <Form.Item name="keyword" label="关键词">
              <Input placeholder="债权名称/编号" allowClear style={{ width: isMobile ? '100%' : 200 }} />
            </Form.Item>
            <Form.Item name="debt_type" label="类型">
              <Select allowClear style={{ width: isMobile ? '100%' : 180 }} options={toSelectOptions(DebtTypeOptions)} />
            </Form.Item>
            <Form.Item name="date_range" label="发生日">
              <DatePicker.RangePicker allowEmpty={[true, true]} style={{ width: isMobile ? '100%' : undefined }} />
            </Form.Item>
            <Form.Item>
              <Space
                direction={isMobile ? 'vertical' : 'horizontal'}
                style={{ width: isMobile ? '100%' : undefined }}
              >
                <Button type="primary" htmlType="submit" block={isMobile}>查询</Button>
                <Button onClick={handleReset} block={isMobile}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Card className="card-compact" size="small">
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={records}
            columns={columns}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: PAGINATION_ENABLE_SIZE_CHANGER,
              pageSizeOptions: PAGINATION_PAGE_SIZE_OPTIONS,
            }}
            onChange={(pag, filters, sorterArg) => {
              const order = sorterArg?.order ? (sorterArg.order === 'ascend' ? 'asc' : 'desc') : sorter.order;
              const field = sorterArg?.field || sorter.field;
              const nextSorter = { field, order };
              setSorter(nextSorter);
              const nextPagination = { current: pag.current, pageSize: normalizePageSize(pag.pageSize), total: pagination.total };
              setPagination(nextPagination);
              fetchList(nextPagination, nextSorter);
            }}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedDebtId ? [selectedDebtId] : [],
              onChange: (keys) => setSelectedDebtId(keys[0]),
              getCheckboxProps: (record) => ({ disabled: record.status !== 'PUBLISHED' || record.collectionStatus === 'PAUSED' || record.collectionStatus === 'CLOSED' })
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default PublishNoticeSelectDebtPage;
